// DS5.1
import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { Url, URLS } from '@wix/da-url';
import Logo from '@wix/da-ds/pkg/Logo';
import { useSelector } from 'react-redux';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import DreamUpStars from '@wix/da-ds/pkg/Icons/24x24/DreamUpStars';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import {
  BiData,
  TopNavClickBiEvent,
  BuyCoreButtonBiEvent,
  RedirectToLoginBiEvent,
} from '@wix/da-bi/pkg/events';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useMediaQuery } from '@wix/da-hooks/pkg/useMediaQuery';
import { Header } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import {
  getCurrentUserHasCore,
  getTheme,
  getThemeSkin,
} from '../../publicSession/selectors';
import SiteHeaderPlacement from '../../DealerAd/Placement/SiteHeader';
import BuyCoreTextButton from '../../Button/core/dsPresets/BuyCoreTextButton';
import { useCurrentPromo } from '../../utils/hooks/useCurrentPromo';
import HeaderSearchInput from '../_partials/HeaderSearchInput';
import Burger from '../_partials/Burger';
import CartIconButton from '../_partials/CartIconButton';
import MoreMenu from '../_partials/MoreMenu';
import WatchButton from '../_partials/WatchButton';
import SubmitMenu from '../_partials/SubmitMenu';
import UserMenuDesktop from '../_partials/UserMenu/UserMenuDesktop';
import NotificationsDropdownWrapper from '../_partials/NotificationsDropdownWrapper';
import { Props as BaseSiteHeaderProps } from '../SiteHeader';
import { getBackgroundVariant } from '../../DeviationBackground/redux/helpers';
import DamzDropdownWrapper from '../_partials/DamzDropdownWrapper';
import LanguageSelector from '../../LanguageSelector/LanguageSelector';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

import s from './SiteHeaderDesktop.scss';

export interface Props extends BaseSiteHeaderProps {
  hasCoreMembership?: boolean;
  isLoggedIn?: boolean;
  chatCount: number;
  cartCount: number;
  canSeeOldWatch?: boolean;
}

export const DEALER_PROD_REALESTATEID = '9c14ea7a-8c35-429a-9857-00ad04ebb4c1';

const SiteHeader: React.FC<Props> = ({
  searchQuery,
  searchBasePath,
  hasCoreMembership,
  isLoggedIn,
  disableThemeSwitcher,
  showNetworkBar = true,
  cartCount,
  canSeeOldWatch,
}) => {
  const { t } = useTranslation();
  const hasCore = useSelector(getCurrentUserHasCore);
  const themeVariant = useSelector(state =>
    getBackgroundVariant(getTheme(state), getThemeSkin(state))
  );

  const shouldHideCoreButtonDiscount = useMediaQuery('only screen and (max-width: 1200px) ');
  const logoUrl = Url.buildFromPath(URLS.ROOT);
  const canSeeWatchButton = isLoggedIn && canSeeOldWatch;

  const coreCta = (
    <BuyCoreTextButton
      biData={BiData<BuyCoreButtonBiEvent>({
        evid: 375,
        referral_info: ReferralType.HEADER,
      })}
      size="large"
      className={s['buy-core-button']}
      withSaleSparkles
      suppressPromo={shouldHideCoreButtonDiscount}
    >
      <span className={s['long-copy']}>{t('siteHeader.upgradeToCore')}</span>
      <span className={s['short-copy']}>{t('common.getCore')}</span>
    </BuyCoreTextButton>
  );

  // for non-core, show the discount promo instead if there is one
  const noPromoAvailable = !useCurrentPromo('self');
  const showDreamUpLink = !isLoggedIn || hasCore || noPromoAvailable;

  const isLocalize = useFlag('localize');

  return (
    <ErrorBoundary debugComponent="SiteHeaderDesktop">
      <Header role="banner" className={s['root']}>
        <VisuallyHidden>
          <Heading>{t('siteHeader.a11y.heading')}</Heading>
        </VisuallyHidden>
        <ErrorBoundary debugComponent="SiteHeaderDesktopBurger">
          <Burger showNetworkBar={showNetworkBar} iconClassName={s['icon']} />
        </ErrorBoundary>
        <ErrorBoundary debugComponent="SiteHeaderDesktopLeft">
          <BiLink
            biData={BiData<TopNavClickBiEvent>({
              evid: 700,
              link_url: logoUrl,
              link_name: 'logo',
            })}
            href={logoUrl}
            aria-label={t('siteHeader.a11y.home')}
            className={s['logo-link']}
          >
            <Logo
              size="tiny"
              className={s['logo']}
              markColorClassname={s['logo-mark']}
              textColorClassname={s['logo-text']}
              themeVariant={themeVariant}
              allowCustomization={true}
            />
          </BiLink>
          <div className={s['divider']} />
          {canSeeWatchButton && (
            <WatchButton className={c(s['text-button'], s['regular-text-button'])} />
          )}
          <TextButton
            size="medium"
            variant="main"
            className={c(s['text-button'], s['regular-text-button'])}
            href={Url.shopLink()}
            biData={BiData<TopNavClickBiEvent>({
              evid: 700,
              link_url: Url.shopLink(),
              link_name: 'shop',
            })}
          >
            {t('siteHeader.shop')}
          </TextButton>

          <div className={s['divider']} />
          {isLoggedIn && (
            <NotificationsDropdownWrapper
              className={c(s['icon-button'], s['icon-button-has-submenu'])}
              iconClassName={s['icon']}
              numberClassName={s['number']}
            />
          )}
          <HeaderSearchInput
            className={c(s['search-input'])}
            searchQuery={searchQuery}
            searchBasePath={searchBasePath}
          />

          {showDreamUpLink && (
            <PlainButtonOrLink
              className={s['dreamup-link']}
              href={Url.dreamupLink()}
              biData={BiData<TopNavClickBiEvent>({
                evid: 700,
                link_url: Url.dreamupLink(),
                link_name: 'anon_dreamup',
              })}
            >
              <DreamUpStars className={s['dreamup-link-icon']} disableDefaultColors />
              <span className={s['long-copy']}>{t('siteHeader.anonDreamup')}</span>
              <span className={s['short-copy']}>{t('siteHeader.anonDreamup.short')}</span>
            </PlainButtonOrLink>
          )}
          {!showDreamUpLink && !hasCoreMembership && coreCta}
        </ErrorBoundary>
        <div className={s['right-side']}>
          <ErrorBoundary debugComponent="SiteHeaderDesktopRight">
            <SiteHeaderPlacement className={s['ad-slot']} />
            {!isLoggedIn && (
              <>
                <TextButton
                  size="small"
                  variant="main"
                  biData={BiData<RedirectToLoginBiEvent>({
                    evid: 240,
                    referral_info: ReferralType.HEADER,
                  })}
                  className={s['text-button']}
                  href={Url.joinLink()}
                >
                  {t('siteHeader.join')}
                </TextButton>
                <TextButton
                  size="small"
                  variant="main"
                  biData={BiData<TopNavClickBiEvent>({
                    evid: 700,
                    link_url: Url.loginLink(),
                    link_name: 'login',
                  })}
                  className={s['text-button']}
                  href={Url.loginLink()}
                >
                  {t('siteHeader.signIn')}
                </TextButton>
              </>
            )}
            <MoreMenu
              isLoggedIn={!!isLoggedIn}
              className={c(s['icon-button'], s['more-menu-button'])}
              menuClassName={s['more-menu']}
            />
            {isLoggedIn && (
              <DamzDropdownWrapper
                className={c(s['icon-button'], s['icon-button-has-submenu'])}
                iconClassName={s['icon']}
                numberClassName={s['number']}
              />
            )}
            {cartCount > 0 && (
              <CartIconButton
                className={s['icon-button']}
                cartCount={cartCount}
                iconClassName={s['icon']}
                numberClassName={s['number']}
              />
            )}
            {!isLoggedIn && isLocalize && (
              <LanguageSelector className={s['icon-button']} withText={false} />
            )}
            <div className={s['user-icon-button-wrapper']}>
              <UserMenuDesktop
                isOpen={false}
                className={s['icon-submenu']}
                disableThemeSwitcher={disableThemeSwitcher}
              />
            </div>
            <SubmitMenu
              isOpen={false}
              className={c(s['icon-button-has-submenu'], s['icon-button-has-submit-submenu'])}
              menuClassName={s['icon-submenu']}
            />
          </ErrorBoundary>
        </div>
      </Header>
    </ErrorBoundary>
  );
};
SiteHeader.displayName = 'SiteHeader';

export default SiteHeader;
